import { ComponentType, LazyExoticComponent, lazy } from 'react';
import { AppPermissions } from '../types';

export type RouteComponent = ComponentType | LazyExoticComponent<ComponentType>;

const CashCountReport = lazy(() => import('../pages/CashCountReport'));
const CashDiscrepancyReport = lazy(() => import('../pages/CashDiscrepancyReport'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const ExchangePurchaseReport = lazy(() => import('../pages/ExchangePurchaseReport'));
const FixedFundReport = lazy(() => import('../pages/FixedFundReport'));
const Gcs = lazy(() => import('../pages/Gcs'));
const GcsPaymentTypes = lazy(() => import('../pages/GcsPaymentTypes'));
const Help = lazy(() => import('../pages/Help'));
const Profile = lazy(() => import('../pages/Profile'));
const SaleTotals = lazy(() => import('../pages/SaleTotals'));
const SignIn = lazy(() => import('../pages/SignIn'));
const Http403 = lazy(() => import('../pages/Http403'));
const Http404 = lazy(() => import('../pages/Http404'));
const Reprocess = lazy(() => import('../pages/Reprocess'));
const ReprocessNew = lazy(() => import('../pages/ReprocessNew'));
const Closing = lazy(() => import('../pages/Closing'));
const MissingClosingCauses = lazy(() => import('../pages/MissingClosingCauses'));
const MissingControlErrors = lazy(() => import('../pages/MissingControlErrors'));
const AccountingEntries = lazy(() => import('../pages/AccountingEntries'));
const AccountingEntriesNew = lazy(() => import('../pages/AccountingEntriesNew'));
const CompanyCodes = lazy(() => import('../pages/CompanyCodes'));
const CompanyCodeEdit = lazy(() => import('../pages/CompanyCodeEdit'));
const AccountingParameters = lazy(() => import('../pages/AccountingParameters'));
const AccountingParametersEdit = lazy(() => import('../pages/AccountingParametersEdit'));
const MonthClosing = lazy(() => import('../pages/MonthClosing'));
const MonthClosingNew = lazy(() => import('../pages/MonthClosingNew'));
const ReportTender = lazy(() => import('../pages/ReportTender'));
const Reliefs = lazy(() => import('../pages/Reliefs'));
const Cancellations = lazy(() => import('../pages/Cancellations'));
const OakBrook = lazy(() => import('../pages/OakBrook'));
const DashboardClosing = lazy(() => import('../pages/DashboardClosing'));
const SalesByProduct = lazy(() => import('../pages/SalesByProduct'));
const GCsByProduct = lazy(() => import('../pages/GCsByProduct'));
const ThirdPartySalesByItems = lazy(() => import('../pages/ThirdPartySalesByItems'));
const DashboardMastersStatus = lazy(() => import('../pages/DashboardMastersStatus'));
const PettyCashDifference = lazy(() => import('../pages/PettyCashDifference'));
const PettyCashDifferenceByPaymentType = lazy(
  () => import('../pages/PettyCashDifferenceByPaymentType')
);
const EconomicAccountingReport = lazy(() => import('../pages/EconomicAccountingReport'));
const PettyCashWithDrawalReport = lazy(() => import('../pages/PettyCashWithdrawalReport'));
const ExtraCashReport = lazy(() => import('../pages/ExtraCashReport'));
const RecoveryReport = lazy(() => import('../pages/RecoveryReport'));
const EnvelopeDetailsReport = lazy(() => import('../pages/EnvelopeDetailsReport'));
const SalesJournal = lazy(() => import('../pages/SalesJournal'));
const MastersImport = lazy(() => import('../pages/MastersImport'));
const MasterImportNewExecution = lazy(() => import('../pages/MasterImportNewExecution'));
const PendingInterfaces = lazy(() => import('../pages/PendingInterfaces'));
const PendingInterfacesNew = lazy(() => import('../pages/PendingInterfacesNew'));
const ExecutePendingInterfaces = lazy(() => import('../pages/ExecutePendingInterfaces'));
const CashMovementReport = lazy(() => import('../pages/CashMovementReport'));
const GcsByTax = lazy(() => import('../pages/GcsByTax'));
const Interfaces = lazy(() => import('../pages/interfaces'));

export interface RouteConfig {
  key: string;
  path: string;
  component: RouteComponent;
  modeModal: boolean;
  menuGroupKey?: string;
}

export const MenuGroupKey = {
  AccountingInfoReport: 'accounting-info-report',
  Gcs: 'gcs',
  PettyCashDifference: 'petty-cash-difference',
} as const;

interface ProtectedRouteConfig extends RouteConfig {
  permissions: AppPermissions[];
  defaultRedirectForPermission?: AppPermissions;
}

const getRouteConfig = (
  key: string,
  path: string,
  component: RouteComponent,
  modeModal = false
): RouteConfig => ({
  key,
  path,
  component,
  modeModal,
});

const getProtectedRouteConfig = (
  key: string,
  path: string,
  component: RouteComponent,
  defaultRedirectForPermission: AppPermissions | undefined = undefined,
  modeModal = false,
  permissions: AppPermissions[] = [AppPermissions.SALES_TOTALS]
): ProtectedRouteConfig => ({
  ...getRouteConfig(key, path, component, modeModal),
  permissions,
  defaultRedirectForPermission,
});

const protectedRoutes = {
  saleTotals: getProtectedRouteConfig('sale-totals', '/sale-totals', SaleTotals, undefined, false, [
    AppPermissions.SALES_TOTALS,
  ]),
  salesByProduct: getProtectedRouteConfig(
    'sales-product',
    '/sales-product',
    SalesByProduct,
    undefined,
    false,
    [AppPermissions.SALES_TOTALS]
  ),
  gcsByProduct: getProtectedRouteConfig(
    'gcs-product',
    '/gcs-product',
    GCsByProduct,
    undefined,
    false,
    [AppPermissions.GCS]
  ),
  thirdPartySalesByItems: getProtectedRouteConfig(
    'third-party-sales-by-items',
    '/third-party-sales-by-items',
    ThirdPartySalesByItems,
    undefined,
    false,
    [AppPermissions.SALES_TOTALS]
  ),
  reportTender: getProtectedRouteConfig(
    'report-tender',
    '/report-tender',
    ReportTender,
    undefined,
    false,
    [AppPermissions.TENDER]
  ),
  reliefs: getProtectedRouteConfig('reliefs', '/reliefs', Reliefs, undefined, false, [
    AppPermissions.RELIEFS,
  ]),
  cancellations: getProtectedRouteConfig(
    'cancellations',
    '/cancellations',
    Cancellations,
    undefined,
    false,
    [AppPermissions.CANCELLATIONS]
  ),
  gcs: {
    ...getProtectedRouteConfig('gcs', '/gcs', Gcs, undefined, false, [AppPermissions.GCS]),
    menuGroupKey: MenuGroupKey.Gcs,
  },
  gcsPaymentTypes: {
    ...getProtectedRouteConfig(
      'gcs-payment-types',
      '/gcs-payment-types',
      GcsPaymentTypes,
      undefined,
      false,
      [AppPermissions.GCS]
    ),
    menuGroupKey: MenuGroupKey.Gcs,
  },

  gcsByTax: {
    ...getProtectedRouteConfig('gcs-taxes', '/gcs-taxes', GcsByTax, undefined, false, [
      AppPermissions.GCS,
    ]),
    menuGroupKey: MenuGroupKey.Gcs,
  },
  profile: getProtectedRouteConfig('profile', '/profile', Profile, undefined, false),
  help: getProtectedRouteConfig('help', '/help', Help, undefined, false),
  oakBrook: getProtectedRouteConfig('oak-brook', '/oak-brook', OakBrook, undefined, false, [
    AppPermissions.OAK_BROOK_SALES,
  ]),
  pettyCashDifference: {
    ...getProtectedRouteConfig(
      'petty-cash-difference',
      '/petty-cash-difference',
      PettyCashDifference,
      undefined,
      false,
      [AppPermissions.PETTY_CASH_DIFFERENCE]
    ),
    menuGroupKey: MenuGroupKey.PettyCashDifference,
  },
  PettyCashDifferenceByPaymentType: {
    ...getProtectedRouteConfig(
      'petty-cash-difference-by-payment-type',
      '/petty-cash-difference-by-payment-type',
      PettyCashDifferenceByPaymentType,
      undefined,
      false,
      [AppPermissions.PETTY_CASH_DIFFERENCE]
    ),
    menuGroupKey: MenuGroupKey.PettyCashDifference,
  },
  EconomicAccountingReport: getProtectedRouteConfig(
    'economic-accounting-report',
    '/economic-accounting-report',
    EconomicAccountingReport,
    undefined,
    false,
    [AppPermissions.ECONOMIC_CONTABLE]
  ),
  EnvelopeDetailsReport: getProtectedRouteConfig(
    'envelope-details-report',
    '/envelope-details-report',
    EnvelopeDetailsReport,
    undefined,
    false,
    [AppPermissions.CASH_MOVEMENT]
  ),
  salesJournal: getProtectedRouteConfig(
    'sales-journal',
    '/sales-journal',
    SalesJournal,
    undefined,
    false,
    [AppPermissions.SALES_TOTALS]
  ),
  mastersImport: getProtectedRouteConfig(
    'masters-import',
    '/masters-import',
    MastersImport,
    undefined,
    false,
    [AppPermissions.DASHBOARD_MASTER_IMPORT]
  ),
  pendingInterfaces: getProtectedRouteConfig(
    'pending-interfaces',
    '/pending-interfaces',
    PendingInterfaces,
    undefined,
    false,
    [AppPermissions.SALES_TOTALS]
  ),
  interfaces: getProtectedRouteConfig('interfaces', '/interfaces', Interfaces, undefined, false, [
    AppPermissions.SALES_TOTALS,
  ]),
  CashMovementReport: getProtectedRouteConfig(
    'report-cash-movements',
    '/report-cash-movements',
    CashMovementReport,
    undefined,
    false,
    [AppPermissions.CASH_MOVEMENT]
  ),
  pettyCashWithDrawalReport: {
    ...getProtectedRouteConfig(
      'petty-cash-withdrawal-report',
      '/petty-cash-withdrawal-report',
      PettyCashWithDrawalReport,
      undefined,
      false,
      [AppPermissions.ACCOUNTING_INFORMATION]
    ),
    menuGroupKey: MenuGroupKey.AccountingInfoReport,
  },
  cashCountReport: {
    ...getProtectedRouteConfig(
      'cash-count-report',
      '/cash-count-report',
      CashCountReport,
      undefined,
      false,
      [AppPermissions.SALES_TOTALS]
    ),
    menuGroupKey: MenuGroupKey.AccountingInfoReport,
  },
  recoveryReport: {
    ...getProtectedRouteConfig(
      'recovery-report',
      '/recovery-report',
      RecoveryReport,
      undefined,
      false,
      [AppPermissions.SALES_TOTALS]
    ),
    menuGroupKey: MenuGroupKey.AccountingInfoReport,
  },
  exchangePurchaseReport: {
    ...getProtectedRouteConfig(
      'excahnge-purchase-report',
      '/exchange-purchase-report',
      ExchangePurchaseReport,
      undefined,
      false,
      [AppPermissions.SALES_TOTALS]
    ),
    menuGroupKey: MenuGroupKey.AccountingInfoReport,
  },
  fixedFundReport: {
    ...getProtectedRouteConfig(
      'fixed-fund-report',
      '/fixed-fund-report',
      FixedFundReport,
      undefined,
      false,
      [AppPermissions.SALES_TOTALS]
    ),
    menuGroupKey: MenuGroupKey.AccountingInfoReport,
  },
  cashDiscrepancyReport: {
    ...getProtectedRouteConfig(
      'cash-discrepancy-report',
      '/cash-discrepancy-report',
      CashDiscrepancyReport,
      undefined,
      false,
      [AppPermissions.SALES_TOTALS]
    ),
    menuGroupKey: MenuGroupKey.AccountingInfoReport,
  },
  extraCashReport: {
    ...getProtectedRouteConfig(
      'extra-cash-report',
      '/extra-cash-report',
      ExtraCashReport,
      undefined,
      false,
      [AppPermissions.ACCOUNTING_INFORMATION]
    ),
    menuGroupKey: MenuGroupKey.AccountingInfoReport,
  },
};

const protectedNestedRoutes = {
  dashboard: {
    index: getProtectedRouteConfig(
      'dashboard',
      '/dashboard',
      Dashboard,
      AppPermissions.SALES_TOTALS,
      false,
      [
        AppPermissions.DASHBOARD_SALES_GCS,
        AppPermissions.DASHBOARD_DAILY_SALES,
        AppPermissions.DASHBOARD_MISSINGS,
        AppPermissions.DASHBOARD_MONTH_CLOSING,
      ]
    ),
    children: {
      dashboardMissingClosingDetail: getProtectedRouteConfig(
        'dashboardClosing',
        'closing',
        DashboardClosing,
        undefined,
        false,
        [AppPermissions.MISSING_CLOSING_DETAIL]
      ),
      dashboardMastersStatus: getProtectedRouteConfig(
        'dashboardMastersStatus',
        'masters-status',
        DashboardMastersStatus,
        undefined,
        false,
        [AppPermissions.MISSING_CLOSING_DETAIL]
      ),
    },
  },
  reprocess: {
    index: getProtectedRouteConfig('reprocess', '/reprocess', Reprocess, undefined, false, [
      AppPermissions.REPROCESS,
    ]),
    children: {
      create: getProtectedRouteConfig('reprocessNew', 'new', ReprocessNew, undefined, false, [
        AppPermissions.REPROCESS,
      ]),
    },
  },
  mastersImport: {
    index: getProtectedRouteConfig(
      'masters-import',
      '/masters-import',
      MastersImport,
      undefined,
      false,
      [AppPermissions.DASHBOARD_MASTER_IMPORT]
    ),
    children: {
      create: getProtectedRouteConfig(
        'masterImportNewExecution',
        'new',
        MasterImportNewExecution,
        undefined,
        false,
        [AppPermissions.DASHBOARD_MASTER_IMPORT]
      ),
    },
  },
  missingClosingDetail: {
    index: getProtectedRouteConfig(
      'missing-closing-detail',
      '/missing-closing-detail',
      Closing,
      undefined,
      false,
      [AppPermissions.MISSING_CLOSING_DETAIL]
    ),
    children: {
      causes: getProtectedRouteConfig(
        'MissingClosingCauses',
        'causes',
        MissingClosingCauses,
        undefined,
        false,
        [AppPermissions.MISSING_CLOSING_DETAIL]
      ),
      errors: getProtectedRouteConfig(
        'MissingControlErrors',
        'error',
        MissingControlErrors,
        undefined,
        false,
        [AppPermissions.MISSING_CONTROL]
      ),
    },
  },

  companyCodes: {
    index: getProtectedRouteConfig(
      'companyCodes',
      '/company-codes',
      CompanyCodes,
      undefined,
      false,
      [AppPermissions.COMPANY_CODES_QUERY]
    ),
    children: {
      edit: getProtectedRouteConfig(
        'companyCodesEdit',
        ':countryIdCompanyId',
        CompanyCodeEdit,
        undefined,
        false,
        [AppPermissions.COMPANY_CODES]
      ),
    },
  },
  accountingParameters: {
    index: getProtectedRouteConfig(
      'accountingParameters',
      '/accounting-parameters',
      AccountingParameters,
      undefined,
      false,
      [AppPermissions.ACCOUNTING_PARAMETERS_QUERY]
    ),
    children: {
      edit: getProtectedRouteConfig(
        'accountingParametersEdit',
        ':countryIdJournalType',
        AccountingParametersEdit,
        undefined,
        false,
        [AppPermissions.ACCOUNTING_PARAMETERS]
      ),
    },
  },
  accountingEntries: {
    index: getProtectedRouteConfig(
      'accountingEntries',
      '/accounting-entries',
      AccountingEntries,
      undefined,
      false,
      [AppPermissions.ACCOUNTING_ENTRIES_COMPLETE, AppPermissions.ACCOUNTING_ENTRIES_QUERY]
    ),
    children: {
      create: getProtectedRouteConfig(
        'accountingEntriesNew',
        'new',
        AccountingEntriesNew,
        undefined,
        false,
        [AppPermissions.ACCOUNTING_ENTRIES_COMPLETE, AppPermissions.ACCOUNTING_ENTRIES_EXECUTION]
      ),
    },
  },
  monthClosing: {
    index: getProtectedRouteConfig(
      'month-closing',
      '/month-closing',
      MonthClosing,
      undefined,
      false,
      [AppPermissions.MONTH_CLOSING]
    ),
    children: {
      create: getProtectedRouteConfig('MonthClosingNew', 'new', MonthClosingNew, undefined, false, [
        AppPermissions.MONTH_CLOSING,
      ]),
    },
  },
  pendingInterfaces: {
    index: getProtectedRouteConfig(
      'execute-pending-interfaces',
      '/execute-pending-interfaces',
      ExecutePendingInterfaces,
      undefined,
      false,
      [AppPermissions.REPROCESS]
    ),
    children: {
      create: getProtectedRouteConfig(
        'pendinInterfacesNew',
        'new',
        PendingInterfacesNew,
        undefined,
        false,
        [AppPermissions.REPROCESS]
      ),
    },
  },
};

const signinRoute = getRouteConfig('signin', '/signin', SignIn);

export default {
  protectedRoutes,
  protectedNestedRoutes,
  signinRoute,
  forbiddenComponent: Http403,
  notFoundComponent: Http404,
};
